<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import dealStatusAPI from 'dashboard/api/pipeline/dealStatus';
import Modal from 'dashboard/components/Modal.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { formatLocalizedCurrency } from '../utils/localeFormatting';
import { getCardCurrency, getCardValue } from '../helpers/columnFilterMatchers';

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  show: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close', 'success']);
const { t, locale } = useI18n();
const { showAlert } = useAlert();
const store = useStore();

const submitting = ref(false);
const selectedReason = ref('');
const commonReasons = ref([]);
const loadingReasons = ref(false);
const reasonsError = ref('');
const submitError = ref('');
const submitAttempted = ref(false);
const reasonSelectRef = ref(null);

const focusReasonSelect = async () => {
  await nextTick();
  reasonSelectRef.value?.$el?.querySelector('select')?.focus();
};

const reasonRequired = computed(() => {
  const pipelineId = props.item?.pipeline_id;
  if (!pipelineId) return false;
  const pipeline = store.getters['pipelines/getPipelines']?.find?.(
    candidate => candidate.id === pipelineId
  );
  return pipeline?.settings?.require_loss_reason === true;
});

const reasonOptions = computed(() =>
  commonReasons.value.map(reason => ({
    value: reason.id,
    label: reason.label,
  }))
);

const itemTitle = computed(
  () =>
    props.item.item_details?.title ||
    props.item.title ||
    t('PIPELINE.UNTITLED_ITEM')
);

const currentAccount = computed(
  () =>
    store.getters['accounts/getAccount']?.(store.getters.getCurrentAccountId) ||
    store.getters.getCurrentAccount ||
    {}
);
const accountCurrency = computed(() =>
  getCardCurrency(
    {
      currency: currentAccount.value.currency,
      item_details: { currency: currentAccount.value.settings?.currency },
    },
    'BRL'
  )
);
const currencyCode = computed(() =>
  getCardCurrency(props.item, accountCurrency.value)
);

const formattedItemValue = computed(() =>
  formatLocalizedCurrency(getCardValue(props.item), {
    currency: currencyCode.value,
    locale: locale.value,
  })
);

async function loadCommonReasons() {
  if (loadingReasons.value) return;

  loadingReasons.value = true;
  reasonsError.value = '';
  try {
    const response = await dealStatusAPI.getCommonReasons();
    commonReasons.value = response.data.reasons || [];
  } catch {
    commonReasons.value = [];
    reasonsError.value = t('PIPELINE.DEAL_STATUS.ERROR.LOAD_REASONS_FAILED');
    showAlert(reasonsError.value, 'error');
  } finally {
    loadingReasons.value = false;
    if (props.show && commonReasons.value.length) await focusReasonSelect();
  }
}

watch(
  () => props.show,
  visible => {
    if (!visible) return;

    selectedReason.value = '';
    submitError.value = '';
    submitAttempted.value = false;
    loadCommonReasons();
  },
  { immediate: true }
);

async function handleConfirm() {
  if (submitting.value) return;

  submitAttempted.value = true;
  if (!selectedReason.value) {
    await focusReasonSelect();
    return;
  }

  submitting.value = true;
  submitError.value = '';
  try {
    const { data } = await dealStatusAPI.markAsLost(
      props.item.id,
      selectedReason.value
    );

    showAlert(t('PIPELINE.DEAL_STATUS.SUCCESS.MARKED_LOST'));
    emit('success', data);
    emit('close');
  } catch {
    submitError.value = t('PIPELINE.DEAL_STATUS.ERROR.MARK_FAILED');
    showAlert(submitError.value, 'error');
  } finally {
    submitting.value = false;
  }
}

function handleCancel() {
  if (!submitting.value) emit('close');
}
</script>

<template>
  <Modal
    :show="show"
    :close-on-backdrop-click="!submitting"
    :close-on-escape="!submitting"
    :show-close-button="!submitting"
    initial-focus-selector="#mark-lost-reason-select"
    @close="handleCancel"
  >
    <section
      class="max-w-md p-4 sm:p-6"
      :aria-busy="loadingReasons || submitting"
      data-modal="mark-as-lost"
    >
      <h2 class="mb-2 pr-10 text-heading-2 text-n-slate-12">
        {{ t('PIPELINE.DEAL_STATUS.LOST_MODAL.TITLE') }}
      </h2>
      <p class="mb-4 text-body-main text-n-slate-11">
        {{ t('PIPELINE.DEAL_STATUS.LOST_MODAL.MESSAGE') }}
      </p>

      <div class="mb-5 rounded-lg border border-n-ruby-9 bg-n-ruby-3 p-4">
        <p class="break-words text-label font-medium text-n-ruby-11">
          {{ itemTitle }}
        </p>
        <p
          v-if="formattedItemValue"
          class="mt-1 text-label-small text-n-ruby-12"
        >
          {{ t('PIPELINE.DEAL_STATUS.LOST_MODAL.VALUE_LABEL') }}
          {{ formattedItemValue }}
        </p>
      </div>

      <div
        v-if="loadingReasons"
        class="flex min-h-28 flex-col items-center justify-center gap-3"
        role="status"
        aria-live="polite"
      >
        <Spinner class="!size-6" />
        <span class="text-sm text-n-slate-11">
          {{ t('PIPELINE.LOADING') }}
        </span>
      </div>

      <div
        v-else-if="reasonsError"
        class="flex min-h-28 flex-col items-center justify-center gap-4 text-center"
        role="alert"
      >
        <p class="text-sm text-n-ruby-11">{{ reasonsError }}</p>
        <Button
          variant="faded"
          color="slate"
          size="lg"
          icon="i-lucide-rotate-ccw"
          :label="t('PIPELINE_MODALS.RETRY')"
          @click="loadCommonReasons"
        />
      </div>

      <div v-else class="mb-6 space-y-2">
        <label
          for="mark-lost-reason-select"
          class="block text-label font-medium text-n-slate-12"
        >
          {{ t('PIPELINE.DEAL_STATUS.LOST_MODAL.REASON_LABEL') }}
          <span
            v-if="reasonRequired"
            class="text-n-ruby-11"
            :title="t('PIPELINE.DEAL_STATUS.LOST_MODAL.REASON_REQUIRED_HINT')"
          >
            <span aria-hidden="true">
              {{ t('PIPELINE.DEAL_STATUS.LOST_MODAL.REQUIRED_MARKER') }}
            </span>
            <span class="sr-only">
              {{ t('PIPELINE.DEAL_STATUS.LOST_MODAL.REASON_REQUIRED_HINT') }}
            </span>
          </span>
        </label>
        <Select
          ref="reasonSelectRef"
          v-model="selectedReason"
          input-id="mark-lost-reason-select"
          name="lost-reason"
          class="w-full [&>select]:!h-12 [&>select]:!w-full"
          :aria-label="t('PIPELINE.DEAL_STATUS.LOST_MODAL.REASON_LABEL')"
          :options="reasonOptions"
          :placeholder="t('PIPELINE.DEAL_STATUS.LOST_MODAL.REASON_PLACEHOLDER')"
          :disabled="submitting"
          :aria-required="reasonRequired"
          :aria-describedby="
            submitAttempted && !selectedReason
              ? 'mark-lost-reason-error'
              : undefined
          "
          :error="
            submitAttempted && !selectedReason
              ? t('PIPELINE_MODALS.REASON_REQUIRED')
              : ''
          "
        />
        <p
          v-if="submitAttempted && !selectedReason"
          id="mark-lost-reason-error"
          class="text-sm text-n-ruby-11"
          role="alert"
        >
          {{ t('PIPELINE_MODALS.REASON_REQUIRED') }}
        </p>
      </div>

      <p
        v-if="submitError"
        class="mb-4 rounded-lg bg-n-ruby-3 p-3 text-sm text-n-ruby-11"
        role="alert"
      >
        {{ submitError }}
      </p>

      <footer
        class="flex flex-col-reverse gap-2 border-t border-n-weak pt-4 sm:flex-row"
      >
        <Button
          variant="outline"
          color="slate"
          size="lg"
          :label="t('PIPELINE.DEAL_STATUS.LOST_MODAL.CANCEL')"
          :disabled="submitting"
          class="w-full flex-1"
          @click="handleCancel"
        />
        <Button
          color="ruby"
          size="lg"
          :label="t('PIPELINE.DEAL_STATUS.LOST_MODAL.CONFIRM')"
          :is-loading="submitting"
          :disabled="submitting || loadingReasons || Boolean(reasonsError)"
          class="w-full flex-1"
          @click="handleConfirm"
        />
      </footer>
    </section>
  </Modal>
</template>
